import { UserRole } from '../features/user'
import { ActiveBalancesData } from '../pages/finSystem/balanceSummary/widgets/activeBalances/types'

export const getNumberFormat = (
  value: string | number,
  fixedNumber: number,
) => {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fixedNumber,
    maximumFractionDigits: fixedNumber,
  })
  // @ts-ignore
  return formatter.format(value)
}

export const formatNumber = (data: ActiveBalancesData) => {
  return getFinsystemNumberFormat(
    data.openingBalance +
      data.turnover.deposit -
      data.turnover.withdrawal -
      data.liabilities.budgetPayment +
      data.investment -
      data.liabilities.dividend -
      data.lpBalance.lpInvest +
      data.lpBalance.lpReturn +
      data.transfers.inInt -
      data.transfers.outInt -
      data.costs.commission +
      data.exchangeDifference +
      data.lpBalance.value,
    2,
  )
}

export const getFinsystemNumberFormat = (
  value: string | number,
  fixedNumber: number,
) => {
  const numberValue = typeof value === 'number' ? value : parseFloat(value)

  // Форматируем число с нужным количеством десятичных знаков
  let formattedNumber = numberValue.toFixed(fixedNumber)

  // Убираем незначащие нули и точку, если она не нужна
  formattedNumber = parseFloat(formattedNumber).toString()

  // Разделяем целую и дробную части
  const [integerPart, decimalPart] = formattedNumber.split('.')

  // Форматируем целую часть с пробелами
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

  // Объединяем целую и дробную часть обратно
  return decimalPart
    ? `${formattedIntegerPart}.${decimalPart}`
    : formattedIntegerPart
}

export const isAllElementsOfSearchTextNotExist = (searchText: {
  [key: string]: string | null | undefined
}) => {
  return Object.values(searchText).every((item) => item === undefined)
}

export const mapSearchText = (searchText: {
  [key: string]: string | null | undefined
}): Record<string, string | null> => {
  return Object.fromEntries(
    Object.entries(searchText)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [
        key,
        value === null ? null : value?.toLowerCase() ?? null,
      ]),
  )
}

export const changedDateFormat = (date: Date) =>
  `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`

export const changedDateFormatfinsystem = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`

export const changedTimeFormat = (date: Date) =>
  `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`

export const getColorDepositBadge = (value: boolean) => {
  return value ? 'success' : 'danger'
}

/**
 * Парсит строку как число, принимая и точку и запятую как десятичный разделитель.
 * Возвращает 0 для пустой строки/невалидного ввода.
 */
export const parseDecimal = (
  value: string | number | null | undefined,
): number => {
  if (value === null || value === undefined || value === '') return 0
  if (typeof value === 'number') return value
  const normalized = String(value).replace(/\s/g, '').replace(',', '.')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Валидатор для Antd Form.Item rules. Принимает и точку и запятую,
 * запрещает несколько разделителей. Возвращает promise для совместимости с rules.
 */
export const decimalValidator =
  (options: { allowEmpty?: boolean; allowNegative?: boolean } = {}) =>
  (_: unknown, raw: unknown): Promise<void> => {
    const { allowEmpty = true, allowNegative = false } = options
    const value = raw == null ? '' : String(raw).trim()
    if (value === '') {
      return allowEmpty
        ? Promise.resolve()
        : Promise.reject('Field is required')
    }
    const stripped = value.replace(/\s/g, '')
    const pattern = allowNegative ? /^-?[\d.,]+$/ : /^[\d.,]+$/
    if (!pattern.test(stripped)) {
      return Promise.reject('Only digits and a decimal separator are allowed')
    }
    const separators = stripped.replace(/[^.,]/g, '')
    if (separators.length > 1) {
      return Promise.reject('Use only one decimal separator')
    }
    return Promise.resolve()
  }

/**
 * Форматирует дату+время в строку для бэкенда: YYYY-MM-DD HH:mm:ss.
 * Не сдвигает по таймзоне — берёт компоненты ровно как пришло.
 */
export const formatTransactionDateTime = (date: string, time: string): string =>
  `${date} ${time}`

/**
 * Парсит дату/время сервера как «наивную» (без таймзоны), чтобы
 * 14:03:05 в БД отображалось как 14:03:05 в форме, без сдвига на MSK.
 */
export const parseNaiveDateTime = (value: string | null | undefined) => {
  if (!value) return null
  const m = String(value).match(
    /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/,
  )
  if (!m) return null
  const [, y, mo, d, h, mi, s] = m
  return { date: `${y}-${mo}-${d}`, time: `${h}:${mi}:${s}` }
}

export const HEADWAY_PARTNERS_DOMAIN = 'headway.partners'

const URL_PROTOCOL_RE = /^[a-z][a-z0-9+.-]*:\/\//i

/**
 * Проверяет, что домен ссылки совпадает с переданным (по умолчанию headway.partners).
 * Строки, которые не парсятся как URL, считаются совпадающими, чтобы не блокировать
 * сохранение произвольного текста в поле ссылки.
 */
export const isUrlDomainMatching = (
  value: string,
  domain: string = HEADWAY_PARTNERS_DOMAIN,
): boolean => {
  const trimmed = value?.trim()
  if (!trimmed) return true

  const hasProtocol = URL_PROTOCOL_RE.test(trimmed)

  try {
    const url = new URL(hasProtocol ? trimmed : `https://${trimmed}`)
    return url.hostname === domain
  } catch {
    return true
  }
}

export const isElemAvailableForRoles = (
  roles: string[],
  currentUserRoles: UserRole,
) => {
  return roles.some((role) => currentUserRoles.indexOf(role) >= 0)
}

export const isLessThan24HoursAgo = (dateTimeStr: string): boolean => {
  // 1. Преобразуем строку в объект Date
  const targetDate = new Date(dateTimeStr)

  // 2. Получаем текущую дату
  const now = new Date()

  // 3. Вычисляем разницу в миллисекундах
  const diffMs = now.getTime() - targetDate.getTime()

  // 4. Переводим миллисекунды в часы
  const diffHours = diffMs / (1000 * 60 * 60)

  // 5. Проверяем, что прошло меньше 24 часов
  return diffHours < 24
}

export const formatDigits = (num: number, locale: string = 'ru-RU'): string => {
  return new Intl.NumberFormat(locale).format(num)
}
export const formatMoney = (
  value?: string | number | null,
  currency?: string,
  locale: string = 'en-US',
) => {
  if (value === null || value === undefined) return '—'

  const numberValue = typeof value === 'number' ? value : Number(value)

  if (!currency) {
    return getFinsystemNumberFormat(numberValue, 2)
  }

  try {
    // пробуем форматирование через Intl
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).formatToParts(numberValue)

    const fractionDigits =
      parts.find((p) => p.type === 'fraction')?.value.length ?? 0

    return `${getFinsystemNumberFormat(
      numberValue,
      fractionDigits,
    )} ${currency}`
  } catch {
    // если валюта не поддерживается (крипта или неизвестная)
    const fractionDigits = numberValue < 1 ? 8 : 2
    return `${getFinsystemNumberFormat(
      numberValue,
      fractionDigits,
    )} ${currency}`
  }
}
